import React, { useState, useEffect  } from 'react';
import { Card, Button, Form, Spinner, Alert } from 'react-bootstrap';
import OnboardingLayout from '../OnboardingLayout';
import AnimatedStep from './AnimatedStep';
import axios from '../../../api/axios';
import endpoints from '../../../api/endpoints';
import { getRandQuestion } from '../../../services/onboardingApi';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { checkAuth } from '../../../redux/auth/authSlice';
import { toast } from 'react-toastify';
import '../../../styles/Onboarding.css';


const StepDynamicQuestions = ({ data, setData, setStepValidity }) => {
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);


  console.log("StepDynamicQuestions user", user);
  /*
  useEffect(() => {
    // 🧨 TEMP: Force clear buggy persisted data on mount
    localStorage.removeItem('answerHistory');
    localStorage.removeItem('currentQuestion');
  }, []);
  */


  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [fetchedIds, setFetchedIds] = useState(new Set());

  const [answerHistory, setAnswerHistory] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('onboardingAnswerHistory')) || [];
    } catch {
      return [];
    }
  });

  // Store history to localStorage on change
  useEffect(() => {
    localStorage.setItem('onboardingAnswerHistory', JSON.stringify(answerHistory));
  }, [answerHistory]);

  const saveHistory = (newHistory) => {
    console.log("StepDynamicQuestions saveHistory newHistory", newHistory);
    setAnswerHistory(newHistory);
    localStorage.setItem('onboardingAnswerHistory', JSON.stringify(newHistory));
  };

  const cleanHistory = () => {
    setAnswerHistory([]);
    localStorage.removeItem('onboardingAnswerHistory');
    localStorage.removeItem('currentQuestion');
  }

  const fetchNextQuestion = async (history = answerHistory, attempt = 0) => {
    
    console.log("StepDynamicQuestions fetchNextQuestion answerHistory", answerHistory);
    console.log("StepDynamicQuestions fetchNextQuestion history", history);

    if (attempt >= 5) {
      toast.warning('Maximale Anzahl an Fragen erreicht.');
      setCurrentQuestion(null);
      setStepValidity(true);
      setLoading(false);
      cleanHistory();
      return;
    }
   
    try {
      setLoading(true);
      setError(null);

      const response = await getRandQuestion(user.id);
      const question = response.data;
      console.log("StepDynamicQuestions fetchNextQuestion question", question);

      if (!question) {
        setCurrentQuestion(null);
        setStepValidity(true);
        setLoading(false);
        cleanHistory();
        return;
      }

      if (fetchedIds.has(question.id) || history.some(q => q.questionId === question.id && q.submitted)) {
        // Already fetched or answered
        return fetchNextQuestion(history, attempt + 1);
      }

      setFetchedIds((prev) => new Set(prev).add(question.id));
      setCurrentQuestion(question);
      setSelectedAnswers([]);
      setStepValidity(false);
      localStorage.setItem('currentQuestion', JSON.stringify(question));

    } catch (error) {
      console.error('Error fetching question:', error);
      setError('Fehler beim Laden der Frage.');
    } finally {
      setLoading(false); // ✅ Always stop loading
    }
  };
  

  useEffect(() => {
    
    const init = async () => {
      try {
        const savedQuestion = localStorage.getItem('currentQuestion');
        const cachedQuestion = savedQuestion ? JSON.parse(savedQuestion) : null;
        
        if (cachedQuestion && !answerHistory.some(q => q.questionId === cachedQuestion.id && q.submitted)) {
          setFetchedIds(new Set([cachedQuestion.id]));
          setCurrentQuestion(cachedQuestion);
          setSelectedAnswers([]);
          setStepValidity(false);
          setLoading(false);
        } else {
          await fetchNextQuestion();
        }
      } catch (err) {
        console.error('Auth Failied! error:', err);
        toast.error("Session abgelaufen. Bitte erneut anmelden.");
        navigate('/login');
      }
    };
    
    init();
    
  }, []);

  const handleAnswerChange = (e, optionId) => {
    const isChecked = e.target.checked;
    const selectionType = currentQuestion?.selectionType?.toUpperCase();
    const maxSelections = currentQuestion?.maxSelections || Infinity;

    setSelectedAnswers((prev) => {
      if (selectionType === 'SINGLE') {
        return [optionId];
      }

      if (isChecked) {
        return prev.length < maxSelections ? [...prev, optionId] : prev;
      } else {
        return prev.filter((id) => id !== optionId);
      }
    });
  };

  const handleSubmitAnswer = async () => {
    if (!currentQuestion || selectedAnswers.length === 0) return;

    // Prevent resubmission if already answered
    const alreadySubmitted = answerHistory.some(
      (entry) => entry.questionId === currentQuestion.id && entry.submitted
    );
    if (alreadySubmitted) {
      console.warn('Question already answered, skipping submission.');
      await fetchNextQuestion(answerHistory);
      return;
    }

    try {
      setSubmitting(true);
      const postBody = {
        questionId: currentQuestion.id,
        selectedOptionIds: selectedAnswers,
      };
      // Make API call to submit the answer
      const response = await axios.post(endpoints.onboarding.answer.save, postBody);
      toast.success("Antwort gespeichert.");
      console.log('Answer submitted successfully:', response);

      // ✅ Mark this question as answered
      const updatedHistory = [
        ...answerHistory,
        { questionId: currentQuestion.id, submitted: true }
      ];
      
      saveHistory(updatedHistory);
      localStorage.removeItem('currentQuestion');
      setCurrentQuestion(null);
      await fetchNextQuestion(updatedHistory); 
      setQuestionIndex((prev) => prev + 1);
    } catch (error) {
      toast.error("Antwort konnte nicht gespeichert werden.");
      setError('Fehler beim Antwort Absenden.');
      console.error('Error submitting answer:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const renderHelpText = () => {
    if (!currentQuestion) return null;
    if (currentQuestion.selectionType === 'SINGLE') return '(Nur eine Antwort möglich)';
    if (currentQuestion.maxSelections) return `(Bis zu ${currentQuestion.maxSelections} Antworten möglich)`;
    return null;
  };

  const renderAnswers = () => {
    if (!currentQuestion?.answerOptions) return null;

    const isMulti = currentQuestion.selectionType === 'MULTIPLE';
    const maxReached = isMulti && selectedAnswers.length >= (currentQuestion.maxSelections || Infinity);


    console.log("currentQuestion", currentQuestion);

    return currentQuestion.answerOptions.map((option) => {
      const isSelected = selectedAnswers.includes(option.id);
      const disabled = submitting  || (isMulti && maxReached && !isSelected);

      return (
        <Form.Check
          key={option.id}
          type={isMulti ? 'checkbox' : 'radio'}
          label={option.label}
          value={option.id}
          checked={isSelected}
          onChange={(e) => handleAnswerChange(e, option.id)}
          disabled={disabled}
          name="dynamic-question"
          className= {isSelected ? 'text-success' : ''}
        />
      );
    });
  };

  return (
    <OnboardingLayout>
      <AnimatedStep>
        <Card className="p-4 shadow-sm">
          {loading ? (
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          ) : error ? (
            <Alert variant="danger">{error}</Alert>
          ) : currentQuestion ? (
            <>
              <Card.Title>
                <Card.Subtitle className="mb-2 text-muted">
                  Frage {questionIndex + 1}
                </Card.Subtitle>
                <p className='text-primary'>
                  {currentQuestion.questionText}{' '}
                  <span className="text-danger">{renderHelpText()}</span>
                </p>
              </Card.Title>
              <Form>
                {renderAnswers()}
                {currentQuestion.selectionType === 'MULTIPLE' &&
                  currentQuestion.maxSelections &&
                  selectedAnswers.length >= currentQuestion.maxSelections && (
                    <div className="text-muted small mt-2">
                      Maximale Anzahl von {currentQuestion.maxSelections} Antworten erreicht.
                    </div>
                )}
              </Form>
              <div className="mt-3 d-flex justify-content-end">
                <Button
                  variant="primary"
                  onClick={handleSubmitAnswer}
                  disabled={selectedAnswers.length === 0 || submitting}
                >
                  {submitting ? (
                    <>
                      <Spinner as="span" size="sm" animation="border" /> Speichern...
                    </>
                  ) : (
                    'Antworten'
                  )}
                </Button>
              </div>
            </>
          ) : (
            <Card.Text className="text-center fs-5">
              🎉 Du hast alle Fragen beantwortet!
            </Card.Text>
          )}
        </Card>
      </AnimatedStep>
    </OnboardingLayout>
  );
};

export default StepDynamicQuestions;

